import { Box, SvgIcon, Typography, Avatar, IconButton, Skeleton } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

// Import the same SVG icons used in the actual navigation header
import LocationIcon from '@hapana/shared/web/styles/assets/svgs/map-marker-alt-light-green.svg'
import ExternalLinkIcon from '@hapana/shared/web/styles/assets/svgs/exchange-al-light-greent.svg'
import SearchIcon from '@hapana/shared/web/styles/assets/svgs/search.svg'

const NAV_OPEN_STORAGE_KEY = 'hapana_nav_open'
const DRAWER_WIDTH_CLOSED = 65 // calc(theme.spacing(8) + 1px) ≈ 65px
const DRAWER_WIDTH_OPEN = 240

// Check localStorage to match the nav's stored open state
const getNavIsOpen = () => {
  if (typeof window === 'undefined') {
    return false
  }
  return localStorage.getItem(NAV_OPEN_STORAGE_KEY) === 'true'
}

// Get cached brand name from Zustand persist storage
const getCachedBrandName = () => {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    const stored = localStorage.getItem('corporate-storage')
    if (stored) {
      const parsed = JSON.parse(stored)
      return parsed?.state?.brandName || null
    }
  } catch {
    return null
  }
  return null
}

// Get cached site name from Zustand persist storage
const getCachedSiteName = () => {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    const stored = localStorage.getItem('site-storage')
    if (stored) {
      const parsed = JSON.parse(stored)
      return parsed?.state?.currentSite?.name || null
    }
  } catch {
    return null
  }
  return null
}

// Number of skeleton nav items to show
const NAV_SKELETON_COUNT = 6

// Styles copied EXACTLY from navigation-header.styles.js
const styles = {
  headerContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: 'gray.0',
    width: '100%',
  },
  companyButton: {
    display: 'flex',
    alignItems: 'center',
    height: '64px',
    width: '100%',
    padding: '12px 16px',
    border: 'none',
  },
  companyIcon: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    flexShrink: 0,
  },
  companyIconPlaceholder: {
    backgroundColor: 'primary.lightest',
    color: 'primary.main',
    fontSize: '0.875rem',
    fontWeight: 600,
  },
  companyName: {
    fontWeight: 600,
    fontSize: '16px',
    lineHeight: '24px',
    color: 'gray.80',
    marginLeft: '12px',
    flex: 1,
    textAlign: 'left',
  },
  chevronButton: {
    padding: 0,
    minWidth: 'auto',
    marginLeft: '-4px',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  chevronIcon: {
    width: '20px',
    height: '20px',
    color: 'primary.main',
  },
  dividerLine: {
    height: '1px',
    width: '100%',
    backgroundColor: 'gray.12',
  },
  locationButton: {
    display: 'flex',
    alignItems: 'center',
    height: '48px',
    width: '100%',
    padding: '12px 16px',
    backgroundColor: 'primary.lightest',
    border: 'none',
    gap: '8px',
  },
  locationIconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
  },
  locationIcon: {
    color: 'primary.main',
    fontSize: '20px',
    width: '20px',
    height: '20px',
  },
  locationText: {
    color: 'primary.main',
    flex: 1,
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  externalButton: {
    padding: 0,
    minWidth: 'auto',
    color: 'info.main',
    marginRight: '8px',
    '&:hover': {
      backgroundColor: 'transparent',
    },
  },
  externalIcon: {
    width: '20px',
    height: '20px',
    fontSize: '20px',
    color: 'primary.main',
  },
  // Nav item styles from navigation.styles.js
  menuIcon: {
    fontSize: 20,
    minWidth: 'auto',
  },
  itemButton: {
    width: '100%',
    minHeight: 36,
    paddingY: 1,
    paddingX: 1.25,
    borderRadius: 1,
    marginBottom: 0.5,
    display: 'flex',
    alignItems: 'center',
  },
  itemIcon: {
    minWidth: 0,
    display: 'flex',
    justifyContent: 'center',
    color: 'gray.64',
  },
  itemLabel: {
    marginLeft: 2.25,
    fontWeight: 600,
    fontSize: 14,
    lineHeight: 1.43,
    color: 'gray.80',
  },
}

/**
 * Navigation Skeleton Component
 *
 * Renders a skeleton that matches the navigation drawer layout exactly.
 * Uses the same SVG icons and EXACT same JSX structure as the actual navigation.
 */
export const NavigationSkeleton = () => {
  const isOpen = getNavIsOpen()
  const width = isOpen ? DRAWER_WIDTH_OPEN : DRAWER_WIDTH_CLOSED
  const brandName = getCachedBrandName()
  const siteName = getCachedSiteName()

  return (
    <Box
      sx={{
        width,
        minWidth: width,
        height: '100vh',
        backgroundColor: 'gray.0',
        borderRight: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header container - EXACT copy from navigation-header.component.jsx */}
      <Box sx={styles.headerContainer}>
        {/* Company button - EXACT copy */}
        <Box sx={styles.companyButton}>
          <Avatar sx={{ ...styles.companyIcon, ...styles.companyIconPlaceholder }}>
            {brandName?.trim()?.charAt(0)?.toUpperCase() || '?'}
          </Avatar>
          {isOpen && (
            <>
              <Typography sx={styles.companyName}>{brandName}</Typography>
              <IconButton
                sx={styles.chevronButton}
                size="small"
                data-testid="navigation-skeleton-chevron-right-icon"
              >
                <ChevronRightIcon sx={styles.chevronIcon} />
              </IconButton>
            </>
          )}
        </Box>

        <Box sx={styles.dividerLine} />

        {/* Location button - EXACT copy */}
        <Box sx={styles.locationButton}>
          <Box sx={styles.locationIconContainer}>
            <SvgIcon component={LocationIcon} sx={styles.locationIcon} />
          </Box>
          {isOpen && (
            <>
              <Typography sx={styles.locationText} variant="header4">
                {siteName || 'Go to location'}
              </Typography>
              {siteName && (
                <IconButton
                  sx={styles.externalButton}
                  size="small"
                  data-testid="navigation-skeleton-external-link-icon"
                >
                  <SvgIcon component={ExternalLinkIcon} sx={styles.externalIcon} />
                </IconButton>
              )}
            </>
          )}
          {!isOpen && (
            <IconButton sx={styles.chevronButton} size="small">
              <ChevronRightIcon sx={styles.chevronIcon} />
            </IconButton>
          )}
        </Box>

        <Box sx={styles.dividerLine} />

        {/* KBar Search Button - EXACT copy */}
        <Box sx={styles.locationButton}>
          <Box sx={styles.locationIconContainer}>
            <SvgIcon component={SearchIcon} sx={styles.locationIcon} />
          </Box>
          {isOpen && (
            <Typography sx={styles.locationText} variant="header4">
              Quick search
            </Typography>
          )}
        </Box>
      </Box>

      {/* Nav items - skeleton placeholders since we don't know permissions yet */}
      <Box sx={{ pt: 2, px: 1.5 }}>
        {[...Array(NAV_SKELETON_COUNT)].map((_, index) => (
          <Box key={index} sx={styles.itemButton}>
            <Box sx={styles.itemIcon}>
              <Skeleton variant="circular" width={20} height={20} />
            </Box>
            {isOpen && (
              <Skeleton
                variant="text"
                width={80 + (index % 3) * 20}
                height={20}
                sx={{ marginLeft: 2.25 }}
              />
            )}
          </Box>
        ))}
      </Box>

      {/* Spacer to push content to fill height */}
      <Box sx={{ flex: 1 }} />
    </Box>
  )
}

export default NavigationSkeleton
